/* ── Hoja FO — FullOffice ─────────────────────────────────────────────────── */
(function () {
  'use strict';

  const JSPDF_URL      = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  const AUTOTABLE_URL  = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
  const LOGO_URL       = 'https://cdn.shopify.com/s/files/1/0580/6014/4814/files/USA_cb64db87-5ccd-4333-97f2-7ed9d32a54b9.png?v=1764119154';

  /* ── Helpers ────────────────────────────────────────────────────────────── */
  function loadScript(src) {
    return new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"]`)) return res();
      const s = document.createElement('script');
      s.src = src; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  async function imgToDataUrl(url) {
    try {
      const u = url.startsWith('//') ? 'https:' + url : url;
      const r = await fetch(u);
      if (!r.ok) return null;
      const blob = await r.blob();
      return await new Promise(res => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = () => res(null);
        fr.readAsDataURL(blob);
      });
    } catch { return null; }
  }

  function getImgSize(dataUrl) {
    return new Promise(res => {
      const img = new Image();
      img.onload = () => res([img.naturalWidth, img.naturalHeight]);
      img.onerror = () => res([200, 200]);
      img.src = dataUrl;
    });
  }

  function fitBox(iw, ih, bw, bh) {
    const r = Math.min(bw / iw, bh / ih);
    return [iw * r, ih * r];
  }

  /* ── Parse description element into segments ─────────────────────────────
     Returns an array of:
       { type: 'text',     content: string }   ← paragraph / plain text
       { type: 'listitem', content: string, prefix: string }  ← bullet / numbered
       { type: 'table',    head: string[][], body: string[][] }
  ─────────────────────────────────────────────────────────────────────────── */
  function parseDescEl(el) {
    const segments = [];
    let pendingText = [];

    function flushText() {
      const joined = pendingText.join(' ').trim();
      if (joined) segments.push({ type: 'text', content: joined });
      pendingText = [];
    }

    function parseTable(node) {
      const head = [], body = [];
      const theadRows = node.querySelectorAll('thead tr');
      if (theadRows.length) {
        theadRows.forEach(tr =>
          head.push([...tr.querySelectorAll('th,td')].map(c => c.textContent.replace(/\s+/g, " ").trim()))
        );
      }
      const tbodyRows = node.querySelectorAll('tbody tr');
      const dataRows  = tbodyRows.length ? tbodyRows : node.querySelectorAll('tr');
      dataRows.forEach((tr, idx) => {
        if (!tbodyRows.length && idx === 0 && head.length === 0) {
          head.push([...tr.querySelectorAll('th,td')].map(c => c.textContent.replace(/\s+/g, " ").trim()));
          return;
        }
        const row = [...tr.querySelectorAll('td,th')].map(c => c.textContent.replace(/\s+/g, " ").trim());
        if (row.some(c => c)) body.push(row);
      });
      segments.push({ type: 'table', head, body });
    }

    function walkNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent.replace(/\s+/g, ' ');
        if (t.trim()) pendingText.push(t.trim());
        return;
      }
      const tag = (node.tagName || '').toUpperCase();

      /* Tables */
      if (tag === 'TABLE') { flushText(); parseTable(node); return; }

      /* Lists — handle each <li> individually with its bullet prefix */
      if (tag === 'UL' || tag === 'OL') {
        flushText();
        let counter = 0;
        node.querySelectorAll(':scope > li').forEach(li => {
          counter++;
          const prefix = tag === 'OL' ? `${counter}.  ` : '•  ';
          const text = li.textContent.replace(/\s+/g, ' ').trim();
          if (text) segments.push({ type: 'listitem', content: text, prefix });
        });
        return;
      }

      /* Block elements — flush accumulated text as its own paragraph */
      const isBlock = /^(P|DIV|H[1-6]|BR|DT|DD|BLOCKQUOTE|FIGURE)$/.test(tag);
      for (const child of node.childNodes) walkNode(child);
      if (isBlock && pendingText.length) {
        const line = pendingText.join(' ').trim();
        if (line) { pendingText = []; segments.push({ type: 'text', content: line }); }
      }
    }

    for (const child of el.childNodes) walkNode(child);
    flushText();
    return segments;
  }

  /* ── PDF generator ──────────────────────────────────────────────────────── */
  async function generatePDF(btn) {
    const title   = btn.dataset.productTitle  || '';
    const vendor  = btn.dataset.productVendor || '';
    const sku     = btn.dataset.productSku    || '';
    const imgUrls = (btn.dataset.productImages || '').split('||').map(s => s.trim()).filter(Boolean);
    const pageUrl = btn.dataset.productUrl    || window.location.href;
    const descSel = (btn.dataset.descSel || '.product-description,.disclosure__content').split(',');

    /* Find description element */
    let descEl = null;
    for (const sel of descSel) {
      descEl = document.querySelector(sel.trim());
      if (descEl) break;
    }

    /* Load jsPDF + autoTable */
    await loadScript(JSPDF_URL);
    await loadScript(AUTOTABLE_URL);
    const { jsPDF } = window.jspdf;

    const PW = 210, PH = 297, MX = 14, CW = PW - MX * 2;
    const HDR_H = 16, FTR_H = 12;
    const BOTTOM = PH - FTR_H - 5;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    /* Fetch logo + product images in parallel */
    const maxImgs = Math.min(imgUrls.length, 3);
    const [logoData, ...imgDatas] = await Promise.all([
      imgToDataUrl(LOGO_URL),
      ...imgUrls.slice(0, maxImgs).map(imgToDataUrl)
    ]);

    /* ── Header ──────────────────────────────────────────────────────────── */
    function drawHeader() {
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, PW, HDR_H, 'F');
      if (logoData) {
        try { doc.addImage(logoData, 'PNG', MX, 2.5, 30, 11, undefined, 'FAST'); } catch (_) {}
      }
      doc.setFontSize(10.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
      doc.text('Hoja membretada', PW - MX, 7.5, { align: 'right' });
      doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(170, 170, 170);
      doc.text('FICHA DE PRODUCTO', PW - MX, 12.5, { align: 'right' });
    }

    /* ── Footer ──────────────────────────────────────────────────────────── */
    function drawFooter(pg, total) {
      const fy = PH - FTR_H;
      doc.setFillColor(0, 0, 0);
      doc.rect(0, fy, PW, FTR_H, 'F');
      doc.setFontSize(5.8); doc.setFont('helvetica', 'normal'); doc.setTextColor(180, 180, 180);
      const contacts = ['L-D 9:00 am – 6:00 pm', 'WA: +52 564 992 1531', 'GDL: (33) 1454 1590',
                        'CDMX: (55) 4440 2951', 'MTY: (81) 3849 0511', 'ayuda@fulloffice.com'];
      doc.text(contacts.join('  ·  '), PW / 2, fy + 5.5, { align: 'center' });
      if (total > 1) {
        doc.setTextColor(120, 120, 120);
        doc.text(`Pág. ${pg} / ${total}`, PW - MX, fy + 9.5, { align: 'right' });
      }
    }

    /* ── Divider ─────────────────────────────────────────────────────────── */
    function divider(y) {
      doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2);
      doc.line(MX, y, PW - MX, y);
      return y + 5;
    }

    /* ── Render a styled table ───────────────────────────────────────────── */
    function renderTable(seg, startY) {
      doc.autoTable({
        head: seg.head.length ? seg.head : undefined,
        body: seg.body,
        startY,
        margin: { left: MX, right: MX, top: HDR_H + 4, bottom: FTR_H + 6 },
        theme: 'grid',
        headStyles: {
          fillColor: [26, 26, 26],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7.5,
          cellPadding: 3,
        },
        bodyStyles: {
          fontSize: 7.5,
          textColor: [50, 50, 50],
          cellPadding: 2.5,
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        styles: {
          lineColor: [210, 215, 220],
          lineWidth: 0.25,
          overflow: 'linebreak',
          font: 'helvetica',
        },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [30, 30, 30] },
        },
        didDrawPage: () => {
          drawHeader();
        },
      });
      return doc.lastAutoTable.finalY + 4;
    }

    /* ── START RENDERING ─────────────────────────────────────────────────── */
    drawHeader();
    let y = HDR_H + 6;

    /* Title */
    doc.setTextColor(17, 17, 17); doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(title, CW);
    doc.text(titleLines, MX, y);
    y += titleLines.length * 5.8 + 2;

    /* Vendor / SKU */
    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
    const meta = [vendor && `Marca: ${vendor}`, sku && `SKU: ${sku}`].filter(Boolean).join('   |   ');
    if (meta) { doc.text(meta, MX, y); y += 6; }

    y = divider(y);

    /* ── Images ──────────────────────────────────────────────────────────── */
    const validImgs = imgDatas.filter(Boolean);
    if (validImgs.length > 0) {
      const GAP = 4, IH = 55;

      if (validImgs.length === 1) {
        const BW = 85, ix = MX + (CW - BW) / 2;
        try {
          const [iw, ih] = await getImgSize(validImgs[0]);
          const [fw, fh] = fitBox(iw, ih, BW, IH);
          doc.addImage(validImgs[0], 'JPEG', ix + (BW - fw) / 2, y + (IH - fh) / 2, fw, fh, undefined, 'FAST');
        } catch (_) {}
      } else {
        const MW = (CW - GAP) * 2 / 3, TW = CW - GAP - MW, TH = (IH - GAP) / 2;
        try {
          const [iw, ih] = await getImgSize(validImgs[0]);
          const [fw, fh] = fitBox(iw, ih, MW, IH);
          doc.addImage(validImgs[0], 'JPEG', MX + (MW - fw) / 2, y + (IH - fh) / 2, fw, fh, undefined, 'FAST');
        } catch (_) {}
        const tx = MX + MW + GAP;
        if (validImgs[1]) try {
          const [iw, ih] = await getImgSize(validImgs[1]);
          const [fw, fh] = fitBox(iw, ih, TW, TH);
          doc.addImage(validImgs[1], 'JPEG', tx + (TW - fw) / 2, y + (TH - fh) / 2, fw, fh, undefined, 'FAST');
        } catch (_) {}
        if (validImgs[2]) try {
          const [iw, ih] = await getImgSize(validImgs[2]);
          const [fw, fh] = fitBox(iw, ih, TW, TH);
          doc.addImage(validImgs[2], 'JPEG', tx + (TW - fw) / 2, y + TH + GAP + (TH - fh) / 2, fw, fh, undefined, 'FAST');
        } catch (_) {}
      }
      y += IH + 5;
    }

    y = divider(y);

    /* ── Badges ──────────────────────────────────────────────────────────── */
    {
      const BH = 6.5, BR = 3.3;
      const badges = [
        { label: 'Factura 100% mexicana',                  color: [26, 26, 26],  fill: [255, 255, 255], border: [26, 26, 26]  },
        { label: 'Envíos de 24 a 72 horas en todo México', color: [30, 126, 52], fill: [240, 255, 244], border: [30, 126, 52] },
      ];
      let bx = MX;
      doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
      for (const b of badges) {
        doc.setTextColor(...b.color);
        const bw = doc.getTextWidth(b.label) + 14;
        doc.setFillColor(...b.fill); doc.setDrawColor(...b.border); doc.setLineWidth(0.4);
        doc.roundedRect(bx, y, bw, BH, BR, BR, 'FD');
        doc.text(b.label, bx + bw / 2, y + BH / 2 + 0.5, { align: 'center', baseline: 'middle' });
        bx += bw + 6;
      }
      doc.setLineWidth(0.2);
      y += BH + 8;
    }

    y = divider(y);

    /* ── Description: text + styled tables ──────────────────────────────── */
    if (descEl) {
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(22, 22, 22);
      doc.text('Descripción', MX, y);
      y += 5;

      const LH = 4.5;
      const segments = parseDescEl(descEl);

      function ensureSpace() {
        if (y + LH > BOTTOM) {
          doc.addPage(); drawHeader(); y = HDR_H + 6;
          doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(68, 68, 68);
        }
      }

      for (const seg of segments) {
        if (seg.type === 'table') {
          /* ── Styled table ── */
          y = renderTable(seg, y);

        } else if (seg.type === 'listitem') {
          /* ── List items omitted from PDF ── */

        } else {
          /* ── Plain paragraph text ── */
          doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(68, 68, 68);
          const lines = doc.splitTextToSize(seg.content, CW);
          for (const line of lines) {
            ensureSpace();
            doc.text(line, MX, y);
            y += LH;
          }
          y += 1.5; // gap between paragraphs
        }
      }
    }

    /* ── Footers on all pages ────────────────────────────────────────────── */
    const totalPgs = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPgs; p++) {
      doc.setPage(p);
      drawFooter(p, totalPgs);
    }

    const fn = title.replace(/[^a-z0-9áéíóúüñ ]/gi, ' ').trim().replace(/\s+/g, '_').substring(0, 55) + '.pdf';
    doc.save(fn);
  }

  /* ── Click handler ───────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    const link = e.target.closest('.ft-pdf-link');
    if (!link || link.classList.contains('is-loading')) return;
    const original = link.textContent;
    link.classList.add('is-loading');
    link.textContent = 'Generando…';
    generatePDF(link)
      .catch(console.error)
      .finally(() => {
        link.classList.remove('is-loading');
        link.textContent = original;
      });
  });

})();
